/**
 * CRMDashboard.jsx
 * Super Admin — CRM > Dashboard
 *
 * Features:
 *  - 12 stats cards (4-per-row on desktop)
 *  - Daily Outreach Line chart (last 7 days: SMS / WhatsApp / Voice)
 *  - Lead Status Doughnut chart
 *  - State-wise Outreach horizontal Bar chart
 *  - Auto-refresh every 60 seconds
 *  - Skeleton loading state
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Users, UserPlus, MessageSquare, MessageCircle, Phone, PhoneCall,
  TrendingUp, Clock, Calendar, CalendarCheck, CheckCircle, Handshake,
  RefreshCw, AlertCircle, BarChart2,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com';

const authHeaders = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('session_token'),
});

// ── Stat card definition ──────────────────────────────────────────────────────
const STAT_CARDS = [
  { key: 'total_contacts',        label: 'Total Contacts',         icon: Users,         color: 'blue' },
  { key: 'contacts_today',        label: 'Contacts Today',         icon: UserPlus,      color: 'green' },
  { key: 'sms_sent',              label: 'SMS Sent Today',         icon: MessageSquare, color: 'yellow' },
  { key: 'whatsapp_sent',         label: 'WhatsApp Sent Today',    icon: MessageCircle, color: 'green' },
  { key: 'voice_calls_initiated', label: 'Voice Calls Today',      icon: Phone,         color: 'purple' },
  { key: 'calls_answered',        label: 'Calls Answered',         icon: PhoneCall,     color: 'green' },
  { key: 'interested_leads',      label: 'Interested Leads',       icon: TrendingUp,    color: 'emerald' },
  { key: 'followup_required',     label: 'Follow-up Required',     icon: Clock,         color: 'orange' },
  { key: 'demo_requests',         label: 'Demo Requests',          icon: Calendar,      color: 'blue' },
  { key: 'demos_scheduled',       label: 'Demos Scheduled',        icon: CalendarCheck, color: 'purple' },
  { key: 'demos_completed',       label: 'Demos Completed',        icon: CheckCircle,   color: 'green' },
  { key: 'partnerships_converted',label: 'Partnerships Converted', icon: Handshake,     color: 'yellow' },
];

const COLOR_MAP = {
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600',    border: 'border-blue-200' },
  green:   { bg: 'bg-green-50',   icon: 'text-green-600',   border: 'border-green-200' },
  yellow:  { bg: 'bg-yellow-50',  icon: 'text-yellow-600',  border: 'border-yellow-200' },
  purple:  { bg: 'bg-purple-50',  icon: 'text-purple-600',  border: 'border-purple-200' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
  orange:  { bg: 'bg-orange-50',  icon: 'text-orange-600',  border: 'border-orange-200' },
};

// ── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 animate-pulse">
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg bg-gray-200" />
      <div className="w-12 h-4 rounded bg-gray-200" />
    </div>
    <div className="w-16 h-7 rounded bg-gray-200 mb-1" />
    <div className="w-24 h-3 rounded bg-gray-100" />
  </div>
);

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.blue;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${c.bg} ${c.border} border flex items-center justify-center`}>
          <Icon size={18} className={c.icon} />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">
        {value !== undefined && value !== null ? Number(value).toLocaleString('en-IN') : '—'}
      </p>
      <p className="text-xs text-gray-500 mt-0.5 font-medium">{label}</p>
    </div>
  );
};

// ── Doughnut lead status colors ───────────────────────────────────────────────
const LEAD_STATUS_COLORS = [
  '#6B7280', '#3B82F6', '#10B981', '#F59E0B', '#F97316',
  '#8B5CF6', '#6366F1', '#059669', '#EF4444', '#94A3B8',
];

// ── Main component ────────────────────────────────────────────────────────────
const CRMDashboard = () => {
  const [stats,      setStats]      = useState(null);
  const [dailyData,  setDailyData]  = useState([]);
  const [leadFunnel, setLeadFunnel] = useState([]);
  const [stateData,  setStateData]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [lastUpdated,setLastUpdated]= useState(null);
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const headers = authHeaders();
      const [statsRes, dailyRes, funnelRes, stateRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/crm/dashboard/stats`,         { headers }),
        axios.get(`${API_BASE_URL}/api/crm/dashboard/daily-outreach`,{ headers }),
        axios.get(`${API_BASE_URL}/api/crm/analytics/lead-funnel`,   { headers }),
        axios.get(`${API_BASE_URL}/api/crm/dashboard/state-wise`,    { headers }),
      ]);
      setStats(statsRes.data?.data?.stats || statsRes.data?.stats || {});
      setDailyData(dailyRes.data?.data?.data || dailyRes.data?.data || []);
      setLeadFunnel(funnelRes.data?.data?.funnel || funnelRes.data?.funnel || []);
      setStateData(stateRes.data?.data?.data || stateRes.data?.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[CRMDashboard] fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load dashboard data.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(true);
    intervalRef.current = setInterval(() => fetchAll(false), 60_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchAll]);

  // ── Chart data builders ───────────────────────────────────────────────────

  const buildDailyChartData = () => {
    const sorted = [...dailyData].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-7);
    const labels = sorted.map((d) =>
      new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
    );
    return {
      labels,
      datasets: [
        {
          label: 'SMS',
          data: sorted.map((d) => d.sms_count || 0),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139,92,246,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#8B5CF6',
        },
        {
          label: 'WhatsApp',
          data: sorted.map((d) => d.whatsapp_count || 0),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16,185,129,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#10B981',
        },
        {
          label: 'Voice',
          data: sorted.map((d) => d.voice_count || 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59,130,246,0.08)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#3B82F6',
        },
      ],
    };
  };

  const buildDoughnutData = () => {
    const labels = leadFunnel.map((f) => f.lead_status || f.status || f.label || 'Unknown');
    const data   = leadFunnel.map((f) => Number(f.count || f.value || 0));
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: LEAD_STATUS_COLORS.slice(0, labels.length),
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderWidth: 3,
      }],
    };
  };

  const buildBarData = () => {
    const sorted = [...stateData]
      .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
      .slice(0, 10);
    return {
      labels: sorted.map((s) => s.state || s.label || 'Unknown'),
      datasets: [{
        label: 'Contacts',
        data: sorted.map((s) => Number(s.count || 0)),
        backgroundColor: 'rgba(132,63,239,0.75)',
        borderColor: '#843FEF',
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      title:  { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { beginAtZero: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 11 } } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
    },
    cutout: '60%',
  };

  const barOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'nearest' },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 11 } } },
      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center">
            <BarChart2 size={18} className="text-[#843FEF]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">CRM Dashboard</h1>
            {lastUpdated && (
              <p className="text-xs text-gray-400 mt-0.5">
                Last updated: {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#843FEF] text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-60 transition-colors shadow-sm"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => fetchAll(true)}
            className="ml-auto text-xs font-semibold underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {loading
          ? Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)
          : STAT_CARDS.map((card) => (
              <StatCard
                key={card.key}
                label={card.label}
                value={stats?.[card.key]}
                icon={card.icon}
                color={card.color}
              />
            ))}
      </div>

      {/* Charts row */}
      {!loading && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">

          {/* Daily Outreach — Line chart (span 2 cols on xl) */}
          <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Daily Outreach — Last 7 Days</h2>
            {dailyData.length === 0 ? (
              <EmptyChart message="No outreach data available." />
            ) : (
              <div className="h-64">
                <Line data={buildDailyChartData()} options={lineOptions} />
              </div>
            )}
          </div>

          {/* Lead Status Doughnut */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Lead Status Distribution</h2>
            {leadFunnel.length === 0 ? (
              <EmptyChart message="No lead funnel data." />
            ) : (
              <div className="h-64">
                <Doughnut data={buildDoughnutData()} options={doughnutOptions} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* State-wise outreach — horizontal bar */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">State-wise Outreach — Top 10</h2>
          {stateData.length === 0 ? (
            <EmptyChart message="No state-wise data available." />
          ) : (
            <div className="h-72">
              <Bar data={buildBarData()} options={barOptions} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Small helper component shown when a chart has no data
const EmptyChart = ({ message }) => (
  <div className="h-64 flex flex-col items-center justify-center text-gray-400">
    <BarChart2 size={32} className="mb-2 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

export default CRMDashboard;
